import { useState } from "react";
import { Link } from "react-router-dom";

import { useAccount } from "@/lib/useAccount";
import { MIN_PASSWORD_LENGTH } from "@/lib/accountsApi";
import { Field, StatusBanner } from "./ui";
import { useSaveState } from "@/lib/useSaveState";

type Mode = "signin" | "register";

/**
 * Sign in, or make an account, without leaving the page.
 *
 * Deliberately inline rather than a separate route: someone arrives here to
 * rate a puzzle, and bouncing them to /login and back would lose their place in
 * a 140-row list.
 */
export function AccountBar({
  prompt = "Sign in to rate puzzles",
}: {
  /** The lead-in beside the buttons. The hub sets its own, since the band it
      sits in has already said what an account is for. */
  readonly prompt?: string;
} = {}) {
  const { session, loading, signIn, register, signOut } = useAccount();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("signin");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Shared with the admin panels; its in-flight ref is what stops a
  // double-clicked "Create account" from registering twice.
  const { saving: busy, message, tone, run, reset } = useSaveState();
  const error = tone === "error" ? message : null;

  if (loading) {
    return <span className="meta opacity-50">…</span>;
  }

  if (session) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <span className="meta opacity-70">
          Signed in as <span className="font-bold opacity-100">{session.username}</span>
        </span>
        {session.is_officer ? (
          <span className="meta border-2 border-ink bg-piece-t px-1.5 py-0.5 text-[0.625rem]">
            Officer
          </span>
        ) : null}
        <Link to="/profile" className="btn btn--ghost px-3 py-1.5 text-xs">
          Profile
        </Link>
        <button type="button" className="btn btn--ghost px-3 py-1.5 text-xs" onClick={signOut}>
          Sign out
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <span className="meta opacity-70">{prompt}</span>
        <button
          type="button"
          className="btn px-3 py-1.5 text-xs"
          onClick={() => {
            setMode("signin");
            setOpen(true);
          }}
        >
          Sign in
        </button>
        <button
          type="button"
          className="btn btn--ghost px-3 py-1.5 text-xs"
          onClick={() => {
            setMode("register");
            setOpen(true);
          }}
        >
          Create account
        </button>
      </div>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const done = await run(async () => {
      await (mode === "register" ? register : signIn)(username, password);
      return true;
    });
    if (done) {
      setOpen(false);
      setUsername("");
      setPassword("");
    }
  };

  return (
    <form onSubmit={submit} className="panel w-full max-w-[34rem] px-5 py-4">
      <div className="mb-3 flex items-center gap-4">
        <h3 className="text-lg">{mode === "register" ? "Create an account" : "Sign in"}</h3>
        <button
          type="button"
          className="meta ml-auto underline-offset-4 hover:underline"
          onClick={() => {
            setMode(mode === "register" ? "signin" : "register");
            reset();
          }}
        >
          {mode === "register" ? "I already have one" : "I need an account"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Username" htmlFor="account-username">
          <input
            id="account-username"
            className="input"
            value={username}
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </Field>
        <Field label="Password" htmlFor="account-password">
          <input
            id="account-password"
            className="input"
            type="password"
            value={password}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </Field>
      </div>

      {mode === "register" ? (
        <p className="mt-2 text-[0.8125rem] leading-snug opacity-60">
          At least {MIN_PASSWORD_LENGTH} characters. Letters, numbers and . _ - in the username.
        </p>
      ) : (
        <p className="mt-2 text-[0.8125rem] leading-snug opacity-60">
          Club officers: use your admin username and password here too.
        </p>
      )}

      <div className="mt-3">
        <StatusBanner tone="error" message={error} prefix={null} />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button type="submit" className="btn px-4 py-2 text-sm" disabled={busy}>
          {busy ? "Working…" : mode === "register" ? "Create account" : "Sign in"}
        </button>
        <button
          type="button"
          className="btn btn--ghost px-4 py-2 text-sm"
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
